import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  AuthApi,
  AuthAuthenticateCommand,
  CheckEmailCommand,
  ForgotPasswordCommand,
  GoogleLoginCommand,
  RefreshTokenCommand,
  ResetPasswordCommand,
} from "api";

import authService from "services/auth";
import analyticsService from "services/analytics";

import { setUserId } from "./userSlice";
import { ActionTypes } from "store/actionTypes";
import { setLoading } from "./globalLoadingSlice";

import { routes } from "routes/routes";

const authApi = new AuthApi();

export interface AuthState {
  isLoading?: boolean;
  isEmailSent?: boolean;
  isSocialMediaLoading?: boolean;
  showForgotPasswordLink?: boolean;
}

const initialState: AuthState = {
  isLoading: false,
  isEmailSent: false,
  isSocialMediaLoading: false,
  showForgotPasswordLink: false,
};

// CHECK EMAIL
export const checkEmail = createAsyncThunk(
  "auth/checkEmail",
  async (body: CheckEmailCommand) => {
    const { data } = await authApi.apiAuthCheckEmailPost(body);

    return data.isNewUser;
  },
);

// AUTH
export const auth = createAsyncThunk(
  "auth/auth",
  async (body: AuthAuthenticateCommand, { dispatch }) => {
    const { data } = await authApi.apiAuthAuthenticatePost(body);
    const { userUid, accessToken, refreshToken } = data;

    dispatch(setUserId(userUid));
    analyticsService().setUserId(userUid);
    authService().setAccessToken(accessToken || "");
    authService().setRefreshToken(refreshToken || "");

    return data;
  },
);

// FORGOT PASSWORD
export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (body: ForgotPasswordCommand) => {
    await authApi.apiAuthForgotPasswordPost(body);
  },
);

// RESET PASSWORD
export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (body: ResetPasswordCommand) => {
    await authApi.apiAuthResetPasswordPost(body);
  },
);

// GOOGLE LOGIN
export const googleLogin = createAsyncThunk(
  "auth/googleLogin",
  async (body: GoogleLoginCommand, { dispatch }) => {
    const { data } = await authApi.apiAuthGoogleLoginPost(body);
    const { userUid, accessToken, refreshToken } = data;

    dispatch(setUserId(userUid));
    analyticsService().setUserId(userUid);
    authService().setAccessToken(accessToken || "");
    authService().setRefreshToken(refreshToken || "");

    return data;
  },
);

// FACEBOOK LOGIN
export const facebookLogin = createAsyncThunk(
  "auth/facebookLogin",
  async (token: string, { dispatch }) => {
    const { data } = await authApi.apiAuthMetaLoginPost({ token });
    const { userUid, accessToken, refreshToken } = data;

    dispatch(setUserId(userUid));
    analyticsService().setUserId(userUid);
    authService().setAccessToken(accessToken || "");
    authService().setRefreshToken(refreshToken || "");
    return data;
  },
);

// REFRESH TOKEN
export const refreshAuthToken = createAsyncThunk(
  "auth/refreshToken",
  async (body: RefreshTokenCommand) => {
    const response = await authApi.apiAuthRefreshTokenPost(body);
    return response.data;
  },
);

// CLEAR SESSION
export const clearSession = createAsyncThunk(
  "auth/clearSession",
  async (redirectPath: string | null, thunkAPI) => {
    const { dispatch } = thunkAPI;

    authService().clearSession();
    dispatch({ type: ActionTypes.RESET_STORE });
    analyticsService().removeUserId();

    redirectPath && window.location.replace(redirectPath);
  },
);

// LOGOUT
export const logout = createAsyncThunk("auth/logout", async (_, thunkAPI) => {
  const { dispatch } = thunkAPI;
  dispatch(setLoading(true));

  try {
    await authApi.apiAuthLogoutPost();
  } finally {
    dispatch(clearSession(routes.auth));
    dispatch(setLoading(false));
  }
});

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setIsEmailSent: (state, action) => {
      state.isEmailSent = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkEmail.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(checkEmail.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(checkEmail.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(auth.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(auth.fulfilled, (state) => {
        state.isLoading = false;
        state.showForgotPasswordLink = false;
      })
      .addCase(auth.rejected, (state) => {
        state.showForgotPasswordLink = true;
        state.isLoading = false;
      })
      .addCase(googleLogin.pending, (state) => {
        state.isSocialMediaLoading = true;
      })
      .addCase(googleLogin.fulfilled, (state) => {
        state.isSocialMediaLoading = false;
      })
      .addCase(googleLogin.rejected, (state) => {
        state.isSocialMediaLoading = false;
      })
      .addCase(facebookLogin.pending, (state) => {
        state.isSocialMediaLoading = true;
      })
      .addCase(facebookLogin.fulfilled, (state) => {
        state.isSocialMediaLoading = false;
      })
      .addCase(facebookLogin.rejected, (state) => {
        state.isSocialMediaLoading = false;
      })
      .addCase(forgotPassword.pending, (state) => {
        state.isLoading = true;
        state.isEmailSent = false;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.isLoading = false;
        state.isEmailSent = true;
      })
      .addCase(forgotPassword.rejected, (state) => {
        state.isLoading = false;
        state.isEmailSent = false;
      })
      .addCase(resetPassword.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(resetPassword.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export const { setIsEmailSent } = authSlice.actions;

export default authSlice.reducer;
